import { CommentReturn } from '../../clients/rust-api-client';

/*
 * Ported from front-porch/src/violation/comments/commentTree.ts, which is
 * the reference implementation. The two apps are separate CRA builds with
 * no shared package, so this is a copy by design — keep it diffable
 * line for line rather than letting the two drift.
 *
 * A member only ever receives the shared thread, so the partition step
 * the manager side performs is a no-op here. The orphan rule still earns
 * its place: the server filters the internal thread out server-side, so a
 * shared reply whose parent is internal arrives with a `parent_id` that
 * resolves to nothing.
 */

/**
 * One comment and the replies rendered beneath it.
 *
 * The API returns a flat list plus `parent_id` and deliberately does not
 * assemble the tree itself — paginating a tree is ill-defined, so the
 * nesting is the client's job. See `back-end-rs/docs/VIOLATION_COMMENTS.md`.
 */
export interface CommentNode {
  comment: CommentReturn;
  replies: CommentNode[];
  /** 0 for a root, 1 for a reply. Never higher — see {@link MAX_DEPTH}. */
  depth: number;
  /**
   * Who this comment actually answered, when that differs from the comment
   * it is displayed under. Set only for a reply that was flattened.
   */
  inReplyTo?: CommentReturn;
}

/**
 * Two visible levels, and no more.
 *
 * The backend imposes no cap, so without one here the first argument
 * between a manager and a homeowner renders as a staircase — and this
 * thread has to stay readable on a phone, which is where a manager writes
 * it. Nothing else in the app nests, so there is no deeper indent scale to
 * inherit either.
 */
export const MAX_DEPTH = 1;

const byCreatedAt = (a: CommentNode, b: CommentNode): number =>
  a.comment.created_at.localeCompare(b.comment.created_at);

/**
 * Assemble a flat, chronological comment list into a two-level tree.
 *
 * Call this **after** partitioning by visibility, never before. Building one
 * tree over both threads would let a shared reply resolve an internal
 * parent, which is the leak the backend's same-visibility parent check
 * exists to prevent.
 */
export const buildCommentTree = (comments: CommentReturn[]): CommentNode[] => {
  const byId = new Map<string, CommentReturn>();
  comments.forEach((c) => byId.set(c.id, c));

  const nodes = new Map<string, CommentNode>();
  comments.forEach((c) =>
    nodes.set(c.id, { comment: c, replies: [], depth: 0 })
  );

  const roots: CommentNode[] = [];

  comments.forEach((comment) => {
    const node = nodes.get(comment.id)!;

    // Walk to the top of this comment's chain. The walk stops at a
    // `parent_id` that resolves to nothing, which happens two ways: a
    // SuperAdmin hard-deleted the parent (the FK is ON DELETE NoAction and
    // nothing reparents the children), or the parent is in the other
    // thread and was filtered out before we got here. Both mean the same
    // thing to a reader — the parent is not on this screen — so both
    // promote the child to a root, silently. A "reply to a deleted
    // comment" tombstone would describe a state nobody can act on, and in
    // the filtered case it would disclose that the internal thread has a
    // comment here at all.
    const seen = new Set<string>([comment.id]);
    let immediateParent: CommentReturn | undefined;
    let ancestor: CommentReturn | undefined;
    let cursor: CommentReturn = comment;
    let cycled = false;

    while (cursor.parent_id) {
      const parent = byId.get(cursor.parent_id);
      // The parent is not on this screen. Stop here and let whatever we
      // reached so far be the ancestor.
      if (!parent) break;
      // A cycle is unreachable through the API. It is handled rather than
      // trusted because the cost of being wrong is a comment that renders
      // nowhere: every member of a cycle resolves an ancestor, so without
      // this none of them would ever be a root and the whole cycle would
      // drop out of the tree silently.
      if (seen.has(parent.id)) {
        cycled = true;
        break;
      }
      seen.add(parent.id);
      if (!immediateParent) immediateParent = parent;
      ancestor = parent;
      cursor = parent;
    }

    if (cycled || !ancestor) {
      roots.push(node);
      return;
    }

    node.depth = MAX_DEPTH;
    // A reply to a reply is flattened onto the root of its chain rather
    // than indented again. Naming who it answered keeps the argument
    // followable without the indent.
    if (immediateParent && immediateParent.id !== ancestor.id) {
      node.inReplyTo = immediateParent;
    }
    nodes.get(ancestor.id)!.replies.push(node);
  });

  // The server already orders by `created_at` ASC. Sorting again keeps the
  // rendered order from depending on that promise, and costs nothing on a
  // list this size.
  roots.sort(byCreatedAt);
  nodes.forEach((node) => node.replies.sort(byCreatedAt));

  return roots;
};
